import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AccountResponse } from "api/auth/data-contracts";
import { createApi } from "util/ApiCreator";
import { AccountController } from "api/auth/AccountController";
import { useTestMode } from "context/TestModeContext";
import { mockAccounts } from "data/mockData";
import { adminTheme, statusColors } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const AccountList = () => {
  const navigate = useNavigate();
  const api = useRef(createApi("account") as AccountController);
  const { isTestMode } = useTestMode();
  const [accounts, setAccounts] = useState<AccountResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isTestMode) {
      setAccounts(mockAccounts);
      setLoading(false);
    } else {
      getData();
    }
  }, [isTestMode]);

  const getData = async () => {
    setLoading(true);
    try {
      const res = await api.current.findAll();
      setAccounts(res || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Paskyros
            </Typography>
            <Typography
              variant="body"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              Valdykite vartotojų paskyras
            </Typography>
          </Box>
          <Tooltip title="Atnaujinti">
            <IconButton
              onClick={() => !isTestMode && getData()}
              sx={{
                bgcolor: adminTheme.colors.background,
                border: `1px solid ${adminTheme.colors.border}`,
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Iš viso paskyrų
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {accounts.length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Administratoriai
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.primary }}
            >
              {accounts.filter((a) => a.roles?.includes("ADMIN")).length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Aktyvios
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.success }}
            >
              {accounts.filter((a) => a.fullyCreated).length}
            </Typography>
          </Card>
        </Box>

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell>Vartotojas</TableCell>
                  <TableCell>Telefonas</TableCell>
                  <TableCell>Sukurta</TableCell>
                  <TableCell>Patvirtintas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <GroupIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography>Nėra paskyrų</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => (
                    <TableRow
                      key={account.id}
                      sx={{
                        cursor: "pointer",
                        transition: adminTheme.transitions.fast,
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.04)",
                        },
                      }}
                      onClick={() => navigate(`/account/${account.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: account.roles?.includes("ADMIN")
                                ? adminTheme.colors.primary
                                : adminTheme.colors.backgroundAlt,
                              color: account.roles?.includes("ADMIN")
                                ? "white"
                                : adminTheme.colors.textSecondary,
                            }}
                          >
                            {account.roles?.includes("ADMIN") ? (
                              <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />
                            ) : (
                              <PersonIcon sx={{ fontSize: 20 }} />
                            )}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 500 }}>
                              {account.email}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: adminTheme.colors.textMuted }}
                            >
                              {account.personCode || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{account.phone || "-"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                          {account.created
                            ? new Date(account.created).toLocaleDateString("lt-LT")
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.fullyCreated ? "Taip" : "Ne"}
                          size="small"
                          sx={{
                            bgcolor: account.fullyCreated
                              ? statusColors.finished.bg
                              : statusColors.cancelled.bg,
                            color: account.fullyCreated
                              ? statusColors.finished.text
                              : statusColors.cancelled.text,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Box>
  );
};

export default AccountList;
